import { useCallback, useMemo, useState } from 'react';
import { DistributionEntry } from '../../../types';
import { colors } from '../../../../../colors';
import './index.css';

interface Props {
  entry: DistributionEntry;
  onTotalChange: (subscriptionId: number, value: number) => void;
  onStableChange: (subscriptionId: number, value: number) => void;
  onAssetChange: (subscriptionId: number, value: number) => void;
  onPercentageChange: (subscriptionId: number, pct: number) => void;
  compact?: boolean;
}

function roundDisplay(val: number, decimals: number): string {
  return Number(val.toFixed(decimals)).toString();
}

const DistributionInputs = ({
  entry,
  onTotalChange,
  onStableChange,
  onAssetChange,
  onPercentageChange,
  compact,
}: Props) => {
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const totalChanged =
    Math.abs(entry.currentTotalBalance - entry.originalTotalBalance) > 0.01;
  const stableChanged =
    Math.abs(entry.currentStableBalance - entry.originalStableBalance) > 0.01;
  const assetChanged =
    Math.abs(entry.currentAssetBalance - entry.originalAssetBalance) > 0.001;
  const pctChanged =
    Math.abs(entry.currentPercentage - entry.originalPercentage) > 0.01;

  const assetExceedsStable = useMemo(() => {
    if (!assetChanged) return false;
    const assetDiff = entry.currentAssetBalance - entry.originalAssetBalance;
    if (assetDiff <= 0) return false;
    const costOfNewAsset = assetDiff * entry.latestPrice;
    return costOfNewAsset > entry.currentStableBalance;
  }, [entry, assetChanged]);

  const handleFocus = useCallback((field: string, currentValue: string) => {
    setFocusedField(field);
    setEditValue(currentValue);
  }, []);

  const handleBlur = useCallback(
    (field: string) => {
      const val = parseFloat(editValue);
      if (!isNaN(val) && val >= 0) {
        if (field === 'total') onTotalChange(entry.subscriptionId, val);
        if (field === 'stable') onStableChange(entry.subscriptionId, val);
        if (field === 'asset') onAssetChange(entry.subscriptionId, val);
        if (field === 'pct')
          onPercentageChange(entry.subscriptionId, Math.min(100, val));
      }
      setFocusedField(null);
    },
    [
      editValue,
      entry.subscriptionId,
      onTotalChange,
      onStableChange,
      onAssetChange,
      onPercentageChange,
    ],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, field: string) => {
      if (e.key === 'Enter') handleBlur(field);
    },
    [handleBlur],
  );

  return (
    <div
      className={`distribution-inputs ${compact ? 'compact' : ''} ${
        entry.isDisabled ? 'disabled' : ''
      }`}
    >
      <div className="distribution-input-group">
        <label>Total ({entry.quoteAsset})</label>
        <input
          type="text"
          value={
            focusedField === 'total'
              ? editValue
              : roundDisplay(entry.currentTotalBalance, 2)
          }
          onFocus={() =>
            handleFocus('total', roundDisplay(entry.currentTotalBalance, 2))
          }
          onBlur={() => handleBlur('total')}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, 'total')}
          disabled={entry.isDisabled}
          style={{ color: totalChanged ? colors.orange : colors.white }}
        />
      </div>
      <div className="distribution-input-group">
        <label>Stable ({entry.quoteAsset})</label>
        <input
          type="text"
          value={
            focusedField === 'stable'
              ? editValue
              : roundDisplay(entry.currentStableBalance, 2)
          }
          onFocus={() =>
            handleFocus('stable', roundDisplay(entry.currentStableBalance, 2))
          }
          onBlur={() => handleBlur('stable')}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, 'stable')}
          disabled={entry.isDisabled}
          style={{ color: stableChanged ? colors.orange : colors.white }}
        />
      </div>
      <div className="distribution-input-group">
        <label>Asset ({entry.baseAsset})</label>
        <input
          type="text"
          value={
            focusedField === 'asset'
              ? editValue
              : roundDisplay(entry.currentAssetBalance, 6)
          }
          onFocus={() =>
            handleFocus('asset', roundDisplay(entry.currentAssetBalance, 6))
          }
          onBlur={() => handleBlur('asset')}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, 'asset')}
          disabled={entry.isDisabled}
          className={assetExceedsStable ? 'input-error' : ''}
          style={{
            color: assetChanged
              ? assetExceedsStable
                ? colors.red
                : colors.orange
              : colors.white,
          }}
        />
      </div>
      <div className="distribution-input-group">
        <label>Pct (%)</label>
        <input
          type="text"
          value={
            focusedField === 'pct'
              ? editValue
              : roundDisplay(entry.currentPercentage, 1)
          }
          onFocus={() =>
            handleFocus('pct', roundDisplay(entry.currentPercentage, 1))
          }
          onBlur={() => handleBlur('pct')}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, 'pct')}
          disabled={entry.isDisabled || entry.isLocked}
          style={{ color: pctChanged ? colors.orange : colors.white }}
        />
      </div>
    </div>
  );
};

export default DistributionInputs;
